import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TODAY, uc } from '../utils/dates';

function VoiceModal({ close, sysPrompt, notify, setSt, setTab }) {
  const [phase, setPhase]   = useState("idle");
  const [tx, setTx]         = useState("");
  const [resp, setResp]     = useState("");
  const [status, setStatus] = useState("Tap to speak");
  const [followUp, setFU]   = useState(null);
  const recogRef = useRef(null);
  const txRef    = useRef("");
  const EXAMPLES = ["Add a vinyl", "Log a workout", "What's my schedule today?", "Add task: call HR", "How's my budget?"];

  const speak = useCallback((text, onEnd) => {
    if (!("speechSynthesis" in window)) { onEnd?.(); return; }
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.rate = 1.05;
    const v = window.speechSynthesis.getVoices().find(v => /Samantha|Google US English/i.test(v.name) || v.lang === "en-US");
    if (v) utt.voice = v;
    utt.onend = () => { setPhase("idle"); setStatus("Tap to speak again"); onEnd?.(); };
    window.speechSynthesis.speak(utt); setPhase("speaking");
  }, []);

  const processText = useCallback(async (text, isFU = false) => {
    setPhase("processing"); setStatus("Thinking…");
    if (isFU && followUp) {
      if (followUp.action === "add_vinyl_detail") {
        const pts = text.split(/[—\-]/);
        const artist = pts[0]?.trim() || text, album = pts[1]?.trim() || "Unknown Album";
        setSt(p => ({ ...p, vinyl: [{ id: `v${Date.now()}`, artist, album, vendor: "Unknown", ordered: TODAY, eta: "", status: "ordered", delivered: false, tracking: "" }, ...p.vinyl] }));
        const r = `Added ${artist} — ${album} to vinyl orders.`;
        setResp(r); setFU(null); speak(r); return;
      }
      if (followUp.action === "add_task_text") {
        setSt(p => ({ ...p, tasks: [{ id: `t${Date.now()}`, text, done: false, bucket: "today", listId: "l1", created: TODAY }, ...p.tasks] }));
        const r = `Task added: "${text}"`; setResp(r); setFU(null); speak(r); return;
      }
    }
    const cmd = /vinyl|record|album/i.test(text) ? "add_vinyl"
      : /\b(add|create).*(task|remind)/i.test(text) ? "add_task"
      : /\blog.*(workout|run|gym)/i.test(text) ? "log_workout"
      : /finance|subscri|budget/i.test(text) ? "show_finance"
      : /health|training|marathon/i.test(text) ? "show_health"
      : "chat";
    if (cmd === "add_vinyl") { const r = "Which artist and album?"; setResp(r); setFU({ action: "add_vinyl_detail" }); speak(r, () => startListening(true)); return; }
    if (cmd === "add_task")  { const r = "What's the task?";         setResp(r); setFU({ action: "add_task_text" });   speak(r, () => startListening(true)); return; }
    if (cmd === "show_finance") setTab("finance");
    if (cmd === "show_health")  setTab("health");
    try {
      const res = await fetch("/api/chat", { method: "POST", headers: {
          "Content-Type": "application/json",
        }, body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 120, system: sysPrompt + "\n\nVOICE MODE: Reply in 1-2 sentences max. No markdown.", messages: [{ role: "user", content: text }] }) });
      const data = await res.json();
      const reply = data.content?.[0]?.text || "Got it.";
      setResp(reply); speak(reply);
    } catch { const r = "Couldn't connect."; setResp(r); speak(r); }
  }, [followUp, sysPrompt, setSt, setTab, speak]);

  const startListening = useCallback((isFU = false) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { notify("Speech recognition unavailable"); return; }
    const recog = new SR();
    recog.continuous = false; recog.interimResults = true; recog.lang = "en-US";
    recogRef.current = recog; txRef.current = "";
    recog.onstart  = () => { setPhase("listening"); setStatus(isFU ? "Listening…" : "Listening…"); setTx(""); setResp(""); };
    recog.onresult = e => { const t = Array.from(e.results).map(r => r[0].transcript).join(""); txRef.current = t; setTx(t); };
    recog.onend    = () => { if (txRef.current) processText(txRef.current, isFU); else { setPhase("idle"); setStatus("Tap to speak"); } };
    recog.onerror  = () => { setPhase("idle"); setStatus("Tap to speak"); };
    recog.start();
  }, [processText, notify]);

  const handleOrb = () => {
    if (phase === "listening") recogRef.current?.stop();
    else if (phase === "idle") startListening();
    else if (phase === "speaking") { window.speechSynthesis?.cancel(); setPhase("idle"); setStatus("Tap to speak"); }
  };

  return (
    <div className="voice-bg" onClick={e => e.target === e.currentTarget && close()}>
      <div style={{ fontSize:"var(--fs-xs)", color: "var(--t3)", fontFamily: "var(--mono)", letterSpacing: ".14em", textTransform: "uppercase" }}>Voice Mode</div>
      <div className={`voice-orb ${phase}`} onClick={handleOrb}>{phase === "listening" ? "🎙" : phase === "processing" ? "⚙" : phase === "speaking" ? "🔊" : "🎤"}</div>
      <div className="voice-status">{status}</div>
      {tx && <div className="voice-tx">"{tx}"</div>}
      {resp && !tx && <div className="voice-resp">{resp}</div>}
      <div className="voice-egs" style={{overflowX:"auto",flexWrap:"nowrap",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",paddingBottom:"var(--sp1)"}}>
        {EXAMPLES.map(ex => <button key={ex} className="voice-eg shrink-0" onClick={() => { setTx(ex); processText(ex); }}>{ex}</button>)}
      </div>
      <button className="voice-close" onClick={close}>Close</button>
    </div>
  );
}

// ─── CALENDAR ─────────────────────────────────────────────────────────────────

export default VoiceModal;
